import { Container, Progress } from '@chakra-ui/react';
import { useGetBuyerOfferAnalysisQuery } from '@client/graphql/__generated__/main-operations';
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCheckPermission } from '~/common/hooks/useCheckPermission';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { BuyerOfferAnalysisProvider } from '../../components/BuyerOfferAnalysis/Context/BuyerOfferAnalysisContext';
import { CopilotKit } from '../../components/CopilotChat';
import { OfferScorecard } from '../../components/OfferScorecard/OfferScorecard';
import {
  FeatureKey,
  useIsFeatureEnabledForPayload,
} from '../../hooks/useIsFeatureEnabledForPayload';

export const BuyerOfferAnalysisPage = () => {
  // TODO(mike): check non-selected org admin as well?
  const {
    user,
    selectedOrganization,
    isSelectedOrgAdmin,
    loadingIsSelectedOrgAdmin,
    loadingOrganizations,
  } = useAppContext();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('isPreview') === 'true';

  const isOfferScorecardEnabled = useIsFeatureEnabledForPayload({
    featureFlagKey: FeatureKey.OFFER_COACH,
    organizationId: selectedOrganization?.id,
    userId: user?.id,
  });
  const navigate = useNavigate();
  const { offerScorecardId } = useParams();

  const { data, loading: getBoaLoading } = useGetBuyerOfferAnalysisQuery({
    variables: {
      uuid: offerScorecardId as string,
    },
  });

  const boaId = data?.getBuyerOfferAnalysis?.id;

  const { isAuthorized: boaCanView, loading: boaCanViewLoading } =
    useCheckPermission(
      'boa.canView',
      {
        skip: getBoaLoading || !boaId,
      },
      [boaId as string]
    );

  const { isAuthorized: boaIsAdmin, loading: boaIsAdminLoading } =
    useCheckPermission('boa.isAdmin', { skip: getBoaLoading || !boaId }, [
      boaId as string,
    ]);

  const loading =
    getBoaLoading ||
    boaCanViewLoading ||
    boaIsAdminLoading ||
    loadingIsSelectedOrgAdmin;

  const canView = Boolean(boaCanView || boaIsAdmin || isSelectedOrgAdmin);
  const canEdit = Boolean(boaIsAdmin);

  useEffect(() => {
    if (
      !loadingOrganizations &&
      !loadingIsSelectedOrgAdmin &&
      isOfferScorecardEnabled === false
    ) {
      navigate('/');
    }

    if (loading) {
      return;
    }

    if (!canView) {
      navigate('/404');
    }
  }, [
    isOfferScorecardEnabled,
    navigate,
    loading,
    canView,
    loadingOrganizations,
    loadingIsSelectedOrgAdmin,
  ]);

  return (
    <CopilotKit threadId={boaId}>
      <BuyerOfferAnalysisProvider isViewOnly={!canEdit || isPreview}>
        {loading ? (
          <Container centerContent maxWidth="100%">
            <Progress isIndeterminate size="xs" width="100%" />
          </Container>
        ) : (
          <OfferScorecard />
        )}
      </BuyerOfferAnalysisProvider>
    </CopilotKit>
  );
};
